import React, { useState, useEffect, useRef } from 'react';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { FaCamera, FaUpload, FaTimes, FaUserTie } from 'react-icons/fa';
import './Timesheet.css';


// Helper function to copy text to clipboard
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      alert('✅ Last week data copied to clipboard!');
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('✅ Last week data copied to clipboard!');
      } catch (err) {
        alert('❌ Failed to copy to clipboard');
      }
      document.body.removeChild(textArea);
    }
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    alert('❌ Failed to copy to clipboard');
  }
};

// Real API call function (replaces mock authenticatedApiCall)
const authenticatedApiCall = async (url, options = {}) => {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include', // Important: Include cookies for authentication
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        // Unauthorized - redirect to login
        console.log('Unauthorized - redirecting to login');
        window.location.href = '/';
        throw new Error('Unauthorized');
      }
      throw new Error(`API call failed: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};



// Real useAuth hook (replaces mock useAuth)
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log('Timesheet - Fetching user data from: /api/user');
        
        // Use relative path like Mainbar.jsx does
        const response = await fetch('/api/user', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        console.log('Timesheet - Response status:', response.status);

        if (response.ok) {
          const userData = await response.json();
          console.log('Timesheet - User data received:', userData);
          console.log('Timesheet - User name:', userData.name);
          console.log('Timesheet - User role:', userData.role);
          
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          console.error('Failed to fetch user data');
          
          // Check if unauthorized - redirect to login
          if (response.status === 401) {
            console.log('Unauthorized - redirecting to login');
            window.location.href = '/';
          }
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

// 🔥 PRODUCTION-SAFE LOGOUT for Timesheet.jsx
  const handleLogout = async () => {
    try {
      console.log('🚪 Timesheet logout initiated...');
      
      // 🔥 STEP 1: Backend logout FIRST
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        await fetch('/auth/logout', {
          method: 'GET',
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        console.log('✅ Backend logout successful');
      } catch (fetchError) {
        console.warn('⚠️ Backend logout error (continuing):', fetchError.message);
      }
      
      // 🔥 STEP 2: Clear ALL browser storage
      try {
        // Clear session storage
        if (typeof Storage !== "undefined" && sessionStorage) {
          const keysToRemove = [
            'auth_user_data',
            'auth_user_role', 
            'auth_last_check',
            'auth_state',
            'auth_manager_info',
            'rate_limit_backoff'
          ];
          
          keysToRemove.forEach(key => {
            sessionStorage.removeItem(key);
          });
          
          sessionStorage.clear();
          console.log('✅ Session storage cleared');
        }
        
        // Clear local storage
        if (typeof Storage !== "undefined" && localStorage) {
          localStorage.clear();
          console.log('✅ Local storage cleared');
        }
        
        // Clear ALL cookies (production domains)
        const domains = [
          '.manurevasolutions.com',
          'leave-management.manurevasolutions.com',
          'api.leave-management.manurevasolutions.com',
          window.location.hostname,
          ''
        ];
        
        const cookiesToClear = [
          'leavemgmt_session',
          'session',
          'auth_token',
          'connect.sid'
        ];
        
        domains.forEach(domain => {
          cookiesToClear.forEach(cookie => {
            // Clear with domain
            document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; SameSite=None; Secure`;
            // Clear without domain
            document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure`;
          });
        });
        
        // Clear all cookies as fallback
        document.cookie.split(";").forEach((c) => {
          const cookie = c.trim();
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          
          domains.forEach(domain => {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}`;
          });
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
        });
        
        console.log('✅ All cookies cleared');
        
      } catch (storageError) {
        console.error('❌ Storage clearing error:', storageError);
      }

      // 🔥 STEP 3: Wait to ensure cleanup completes
      await new Promise(resolve => setTimeout(resolve, 200));
      
      console.log('✅ Cleanup complete, redirecting...');
      
      // 🔥 STEP 4: Force redirect with cache busting
      const timestamp = Date.now();
      const logoutUrl = `/?logout=true&t=${timestamp}`;
      
      console.log('🏠 Redirecting to:', logoutUrl);
      
      // Use replace to prevent back button issues
      window.location.replace(logoutUrl);
      
    } catch (error) {
      console.error('❌ Critical logout error:', error);
      
      // Force redirect even on error
      const timestamp = Date.now();
      window.location.replace(`/?logout=true&error=true&t=${timestamp}`);
    }
  };

  return {
    user,
    handleLogout,
    isAuthenticated,
    isLoading
  };
};

// ===== END OF REPLACEMENT CODE =====
const TimesheetDashboard = () => {
  const { user, handleLogout, isAuthenticated, isLoading } = useAuth();
  // Profile picture states
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const profileFileInputRef = useRef(null);
  const lastClickTime = useRef(0);
  const [showPopup, setShowPopup] = useState(false);
  const [showDayView, setShowDayView] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    hours: '',
    description: '',
    projectTitle: '',
    fromTime: '',
    toTime: ''
  });
  

  const [submittedWeeks, setSubmittedWeeks] = useState(new Set());
  const [recentActivity, setRecentActivity] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  
  
  const getCurrentWeekStart = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(now.getFullYear(), now.getMonth(), diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  };
  
  const [currentWeekStart, setCurrentWeekStart] = useState(getCurrentWeekStart());
  const [currentWeekTotal, setCurrentWeekTotal] = useState(0);
  const [currentMonthTotal, setCurrentMonthTotal] = useState(0);
  const [timesheetEntries, setTimesheetEntries] = useState({});
  
  const getWeekKey = (date) => {
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1));
    return getLocalDateKey(weekStart);
  };

  const getLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDateKey = (dateKey) => {
    const [yearStr, monthStr, dayStr] = dateKey.split('-');
    return new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
  };

  // ✅ Load submitted weeks from API on mount
  useEffect(() => {
    const loadSubmittedWeeks = async () => {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const startKey = getLocalDateKey(thirtyDaysAgo);
        const today = new Date();
        const endKey = getLocalDateKey(today);
        
        const entries = await authenticatedApiCall(`/api/timesheet?start=${startKey}&end=${endKey}`);
        
        const submitted = new Set();
        entries.forEach(entry => {
          if (entry.status === 'submitted' || entry.status === 'approved') {
            const entryDate = parseLocalDateKey(entry.date_key);
            const weekKey = getWeekKey(entryDate);
            submitted.add(weekKey);
          }
        });
        
        setSubmittedWeeks(submitted);
        console.log('✅ Loaded submitted weeks:', Array.from(submitted));
      } catch (err) {
        console.error('Failed to load submitted weeks', err);
      }
    };
    
    if (isAuthenticated) {
      loadSubmittedWeeks();
    }
  }, [isAuthenticated]);

  // Poll for status updates from HR
  useEffect(() => {
    const pollStatusUpdates = async () => {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const startKey = getLocalDateKey(thirtyDaysAgo);
        const today = new Date();
        const endKey = getLocalDateKey(today);
        
        const entries = await authenticatedApiCall(`/api/timesheet?start=${startKey}&end=${endKey}`);
        
        const grouped = entries.reduce((acc, e) => {
          const key = e.date_key;
          const entry = {
            id: e.id,
            fromTime: e.from_time,
            toTime: e.to_time,
            description: e.description || '',
            projectTitle: e.project_title,
            hours: e.hours ?? 0,
            status: e.status || 'draft'
          };
          acc[key] = acc[key] ? [...acc[key], entry] : [entry];
          return acc;
        }, {});
        
        setTimesheetEntries(prevEntries => {
          let hasChanges = false;
          Object.keys(grouped).forEach(dateKey => {
            const newEntries = grouped[dateKey];
            const oldEntries = prevEntries[dateKey] || [];
            
            newEntries.forEach(newEntry => {
              const oldEntry = oldEntries.find(e => e.id === newEntry.id);
              if (oldEntry && oldEntry.status !== newEntry.status) {
                hasChanges = true;
              }
            });
          });
          
          if (hasChanges) {
            return grouped;
          }
          return prevEntries;
        });
        
        const activities = entries
          .map(entry => ({
            id: entry.id,
            text: `${entry.hours}h on ${entry.project_title}`,
            date: entry.date_key,
            status: entry.status || 'draft',
            timestamp: entry.created_at || new Date().toISOString()
          }))
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, 10);
        
        setRecentActivity(activities);
        
      } catch (err) {
        console.error('Failed to poll status updates', err);
      }
    };
    
    if (isAuthenticated) {
      const interval = setInterval(pollStatusUpdates, 10000);
      pollStatusUpdates();
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Load profile picture on mount
  useEffect(() => {
    if (user && isAuthenticated) {
      fetchProfilePicture();
    }
  }, [user, isAuthenticated]);
  // Fetch profile picture
  const fetchProfilePicture = async () => {
    if (!user) return;

    try {
      const response = await fetch('/api/user/profile-picture', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture);
      }
    } catch (err) {
      console.error('Error fetching profile picture:', err);
    }
  };

  // Handle avatar double-click
  const handleAvatarClick = () => {
    const currentTime = Date.now();
    const timeDiff = currentTime - lastClickTime.current;
    
    if (timeDiff < 300) {
      console.log('🖱️ Double click detected - Opening profile picture modal');
      setShowProfilePictureModal(true);
      lastClickTime.current = 0;
    } else {
      lastClickTime.current = currentTime;
    }
  };

  // Handle profile file change
  const handleProfileFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload profile picture
  const handleUploadProfilePicture = async () => {
    if (!previewImage) return;

    setIsUploadingProfile(true);
    try {
      const response = await fetch('/api/user/profile-picture', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          profile_picture: previewImage
        })
      });

      if (response.ok) {
        const data = await response.json();
        setProfilePicture(data.profile_picture);
        setShowProfilePictureModal(false);
        setPreviewImage(null);
        alert('Profile picture updated successfully!');
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to upload profile picture');
      }
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      alert('Failed to upload profile picture');
    } finally {
      setIsUploadingProfile(false);
    }
  };

  // Use default logo
const handleUseEmailAvatar = async () => {
  setIsUploadingProfile(true);
  try {
    const response = await fetch('/api/user/profile-picture', {
      method: 'DELETE',
      credentials: 'include',
    });

    if (response.ok) {
      setProfilePicture(null);
      setShowProfilePictureModal(false);
      setPreviewImage(null);
      alert('Switched to default logo successfully!');
    } else {
      alert('Failed to switch to default logo');
    }
  } catch (err) {
    console.error('Error switching to default logo:', err);
    alert('Failed to switch to default logo');
  } finally {
    setIsUploadingProfile(false);
  }
};

  // Close profile picture modal
  const closeProfilePictureModal = () => {
    setShowProfilePictureModal(false);
    setPreviewImage(null);
  };

  // Get user initials
  const getUserInitials = (name, email) => {
    if (name && name.trim()) {
      return name.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // ✅ NEW: Fetch assigned projects on mount
useEffect(() => {
  const fetchAssignedProjects = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingProjects(true);
      console.log('📋 Fetching assigned projects...');
      
      const projects = await authenticatedApiCall('/api/consultant/assigned-projects');
      
      console.log('✅ Assigned projects:', projects);
      setAssignedProjects(projects || []);
      
    } catch (error) {
      console.error('❌ Failed to fetch assigned projects:', error);
      setAssignedProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };
  
  fetchAssignedProjects();
}, [isAuthenticated]);

  
  // Load timesheet data for current week
  useEffect(() => {
    const fetchWeek = async () => {
      try {
        const start = getLocalDateKey(currentWeekStart);
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(currentWeekStart.getDate() + 6);
        const end = getLocalDateKey(weekEnd);
        
        const entries = await authenticatedApiCall(`/api/timesheet?start=${start}&end=${end}`);
        
        const grouped = entries.reduce((acc, e) => {
          const key = e.date_key;
          const entry = {
            id: e.id,
            fromTime: e.from_time,
            toTime: e.to_time,
            description: e.description || '',
            projectTitle: e.project_title,
            hours: e.hours ?? 0,
            status: e.status || 'draft'
          };
          acc[key] = acc[key] ? [...acc[key], entry] : [entry];
          return acc;
        }, {});
        
        setTimesheetEntries(grouped);
      } catch (err) {
        console.error('Failed to fetch timesheet entries', err);
      }
    };
    
    fetchWeek();
    fetchCurrentWeekTotal();
    fetchCurrentMonthTotal();
  }, [currentWeekStart]);

  const formatDate = (date) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  const getWeekDates = (startDate) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const weekDates = getWeekDates(currentWeekStart);
  const weekEnd = new Date(currentWeekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const fetchCurrentWeekTotal = async () => {
    const realStart = getCurrentWeekStart();
    const realEnd = new Date(realStart);
    realEnd.setDate(realEnd.getDate() + 6);
    const startKey = getLocalDateKey(realStart);
    const endKey = getLocalDateKey(realEnd);
    
    try {
      const entries = await authenticatedApiCall(`/api/timesheet?start=${startKey}&end=${endKey}`);
      const total = entries.reduce((sum, e) => sum + (e.hours ?? 0), 0);
      setCurrentWeekTotal(total);
    } catch (e) {
      console.error('Failed to fetch current week total', e);
    }
  };

  const fetchCurrentMonthTotal = async () => {
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const startKey = getLocalDateKey(monthStart);
      const endKey = getLocalDateKey(monthEnd);
      const entries = await authenticatedApiCall(`/api/timesheet?start=${startKey}&end=${endKey}`);
      const total = entries.reduce((sum, e) => sum + (e.hours ?? 0), 0);
      setCurrentMonthTotal(total);
    } catch (e) {
      console.error('Failed to fetch current month total', e);
    }
  };

  const calculateWeekTotal = () => {
    return weekDates.reduce((total, date) => {
      const dateKey = getLocalDateKey(date);
      const dayEntries = timesheetEntries[dateKey] || [];
      const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours ?? 0), 0);
      return total + dayTotal;
    }, 0);
  };

  const isFutureDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate > today;
  };

  // ✅ Check if current week is submitted
  const isCurrentWeekSubmitted = () => {
    const weekKey = getWeekKey(currentWeekStart);
    return submittedWeeks.has(weekKey);
  };

  // ✅ Check if current week has any entries
  const hasCurrentWeekEntries = () => {
    return weekDates.some(date => {
      const dateKey = getLocalDateKey(date);
      return timesheetEntries[dateKey] && timesheetEntries[dateKey].length > 0;
    });
  };

  // ✅ Prevent adding hours if week is submitted
  const handleAddHoursClick = (date) => {
    if (isFutureDate(date)) {
      alert('Cannot add hours for future dates. Please select today or a past date.');
      return;
    }
    
    if (isCurrentWeekSubmitted()) {
      alert('❌ This week has already been submitted. You cannot add more hours.');
      return;
    }
    
    setSelectedDate(date);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedDate(null);
    setFormData({
      hours: '',
      description: '',
      projectTitle: '',
      fromTime: '',
      toTime: ''
    });
  };

  const handleCloseDayView = () => {
    setShowDayView(false);
    setSelectedDate(null);
  };

  const handleCloseEditPopup = () => {
    setShowEditPopup(false);
    setEditingEntry(null);
    setFormData({
      hours: '',
      description: '',
      projectTitle: '',
      fromTime: '',
      toTime: ''
    });
  };

  const handleDayColumnDoubleClick = (date) => {
    const dateKey = getLocalDateKey(date);
    const dayEntries = timesheetEntries[dateKey] || [];
    
    if (dayEntries.length === 0) {
      alert('No timesheet entries found for this date.');
      return;
    }
    
    setSelectedDate(date);
    setShowDayView(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedDate) {
      alert('Please select a date.');
      return;
    }

    if (!formData.hours || formData.hours <= 0 || formData.hours > 24) {
      alert('Please enter valid hours (between 0 and 24).');
      return;
    }

    const dateKey = getLocalDateKey(selectedDate);
    const newEntry = {
      date_key: dateKey,
      hours: Number(formData.hours),
      description: formData.description.trim(),
      project_title: formData.projectTitle.trim() || 'General Work',
      status: 'draft'
    };

    try {
      const saved = await authenticatedApiCall('/api/timesheet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEntry)
      });
      
      const clientEntry = {
        id: saved.id,
        fromTime: saved.from_time || '09:00',
        toTime: saved.to_time || '17:00',
        description: saved.description || '',
        projectTitle: saved.project_title,
        hours: saved.hours,
        status: saved.status || 'draft'
      };
      
      setTimesheetEntries(prev => ({
        ...prev,
        [dateKey]: [...(prev[dateKey] || []), clientEntry]
      }));
      
      fetchCurrentWeekTotal();
      fetchCurrentMonthTotal();
      handleClosePopup();
      
      alert('✅ Hours added successfully!');
    } catch (err) {
      console.error('Failed saving entry', err);
      alert('Failed to save entry. Please try again.');
    }
  };

  // ✅ FIXED: Enhanced submit week validation
  const handleSubmitWeek = async () => {
    const weekKey = getWeekKey(currentWeekStart);
    
    // CHECK 1: Already submitted?
    if (submittedWeeks.has(weekKey)) {
      alert('❌ This week has already been submitted. You cannot submit it again.');
      return;
    }
    
    // CHECK 2: Has any entries?
    if (!hasCurrentWeekEntries()) {
      alert('❌ No timesheet entries found for this week. Please add hours before submitting.');
      return;
    }
    
    // CHECK 3: Get all draft entries
    const allDraftEntries = [];
    const alreadySubmittedCount = [];
    
    weekDates.forEach(date => {
      const dateKey = getLocalDateKey(date);
      const dayEntries = timesheetEntries[dateKey] || [];
      
      dayEntries.forEach(entry => {
        if (entry.status === 'draft') {
          allDraftEntries.push(entry);
        } else if (entry.status === 'submitted' || entry.status === 'approved') {
          alreadySubmittedCount.push(entry);
        }
      });
    });
    
    if (allDraftEntries.length === 0 && alreadySubmittedCount.length > 0) {
      alert('❌ This week has already been submitted. All entries are marked as submitted or approved.');
      setSubmittedWeeks(prev => new Set([...prev, weekKey]));
      return;
    }
    
    if (allDraftEntries.length === 0) {
      alert('❌ No draft entries found to submit for this week.');
      return;
    }
    
    try {
      const updatePromises = allDraftEntries.map(entry => 
        authenticatedApiCall(`/api/timesheet/${entry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'submitted' })
        })
      );
      
      await Promise.all(updatePromises);
      
      setSubmittedWeeks(prev => new Set([...prev, weekKey]));
      
      setTimesheetEntries(prev => {
        const updated = { ...prev };
        weekDates.forEach(date => {
          const dateKey = getLocalDateKey(date);
          if (updated[dateKey]) {
            updated[dateKey] = updated[dateKey].map(entry => ({
              ...entry,
              status: entry.status === 'draft' ? 'submitted' : entry.status
            }));
          }
        });
        return updated;
      });
      
      setRecentActivity(prev => 
        prev.map(activity => {
          const activityDate = activity.date;
          const activityIsInWeek = weekDates.some(date => getLocalDateKey(date) === activityDate);
          if (activityIsInWeek && activity.status === 'draft') {
            return { ...activity, status: 'submitted' };
          }
          return activity;
        })
      );
      
      alert(`✅ Week submitted successfully! (${allDraftEntries.length} entries submitted)`);
      
    } catch (err) {
      console.error('Failed to submit week', err);
      alert('❌ Failed to submit week. Please try again.');
    }
  };

  const handleCopyLastWeek = async () => {
    try {
      const lastWeekStart = new Date(currentWeekStart);
      lastWeekStart.setDate(lastWeekStart.getDate() - 7);
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekEnd.getDate() + 6);

      const lastStartKey = getLocalDateKey(lastWeekStart);
      const lastEndKey = getLocalDateKey(lastWeekEnd);
      const entries = await authenticatedApiCall(`/api/timesheet?start=${lastStartKey}&end=${lastEndKey}`);

      if (!entries || entries.length === 0) {
        alert('No entries found for last week to copy.');
        return;
      }

      const formattedData = entries.map(entry => {
        return `Date: ${entry.date_key}\nProject: ${entry.project_title}\nHours: ${entry.hours}\nDescription: ${entry.description || 'N/A'}\n-----------------`;
      }).join('\n');

      await copyToClipboard(formattedData);

    } catch (err) {
      console.error('Failed to get last week data:', err);
      alert('Failed to copy last week data. Please try again.');
    }
  };

  const handleDeleteEntry = async (dateKey, entryId) => {
    if (window.confirm('Are you sure you want to delete this timesheet entry?')) {
      try {
        await authenticatedApiCall(`/api/timesheet/${entryId}`, { 
          method: 'DELETE' 
        });
        
        setTimesheetEntries(prev => ({
          ...prev,
          [dateKey]: prev[dateKey]?.filter(entry => entry.id !== entryId) || []
        }));
        
        setRecentActivity(prev => 
          prev.filter(activity => activity.id !== entryId)
        );
        
        fetchCurrentWeekTotal();
        fetchCurrentMonthTotal();
        
        alert('✅ Entry deleted successfully!');
      } catch (err) {
        console.error('Failed to delete entry', err);
        alert('Failed to delete entry. Please try again.');
      }
    }
  };

  const handleEditEntry = (entry, date) => {
    setEditingEntry(entry);
    setSelectedDate(date);
    setFormData({
      hours: entry.hours,
      description: entry.description,
      projectTitle: entry.projectTitle,
      fromTime: entry.fromTime,
      toTime: entry.toTime
    });
    setShowEditPopup(true);
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry || !selectedDate) return;

    if (!formData.hours || formData.hours <= 0 || formData.hours > 24) {
      alert('Please enter valid hours (between 0 and 24).');
      return;
    }

    if (formData.projectTitle.trim() === '') {
      alert('Project Title cannot be empty.');
      return;
    }

    const dateKey = getLocalDateKey(selectedDate);
    const hoursVal = Number(formData.hours);
    
    const payload = {
      date_key: dateKey,
      description: formData.description.trim(),
      project_title: formData.projectTitle.trim(),
      hours: hoursVal
    };

    try {
      const updated = await authenticatedApiCall(`/api/timesheet/${editingEntry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const clientUpdated = {
        id: updated.id || editingEntry.id,
        fromTime: updated.from_time || editingEntry.fromTime,
        toTime: updated.to_time || editingEntry.toTime,
        description: updated.description || '',
        projectTitle: updated.project_title,
        hours: updated.hours ?? hoursVal,
        status: updated.status || 'draft'
      };
      
      setTimesheetEntries(prev => ({
        ...prev,
        [dateKey]: prev[dateKey]?.map(entry => 
          entry.id === editingEntry.id ? clientUpdated : entry
        ) || []
      }));
      
      fetchCurrentWeekTotal();
      fetchCurrentMonthTotal();
      handleCloseEditPopup();
      
      alert('✅ Entry updated successfully!');
    } catch (err) {
      console.error('Failed to update entry', err);
      alert('Failed to update entry. Please try again.');
    }
  };

  const handleLogoutClick = () => {
    handleLogout();
  };

  if (isLoading) {
    return (
      <div className="consult-dashboard-container" style={{backgroundColor: '#f3f4f6', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <style>
        {`
          .consult-activity-list::-webkit-scrollbar {
            width: 6px;
          }
          .consult-activity-list::-webkit-scrollbar-track {
            background: #384bf7ff;
            border-radius: 3px;
          }
          .consult-activity-list::-webkit-scrollbar-thumb {
            background: #384bf7ff;
            border-radius: 3px;
          }
          .consult-activity-list::-webkit-scrollbar-thumb:hover {
            background: #384bf7ff;
          }
        `}
      </style>
      <div className="consult-dashboard-container" style={{backgroundColor: '#f3f4f6'}}>
        <div className="consult-right-column">
          <div className="consult-timesheet-header">
            <div className="consult-header-left">
              <div className="consult-logo-container">
                <div className="consult-logo">
                  <img src="/Images/Manurevalogo.png" alt="m"/>
                </div>
                <span className="consult-company-name">Manureva Digital Solutions</span>
              </div>
            </div>
            <div className="consult-timesheet-header-right">
              <div className="consult-notification-icon" style={{color:'#fffff'}}>
                <img src='/Images/notification.png' alt='🔔'/>
              </div>
              <div className="consult-user-profile">
                <div className="consult-timesheet-user-info">
                  <span className="consult-user-name">{user?.name || user?.full_name || 'User'}</span>
                  <span className="consult-user-role">{user?.role || 'Consultant'}</span>
                </div>
                <div 
  className="consult-user-avatar"
  onClick={handleAvatarClick}
  style={{ cursor: 'pointer' }}
  title="Double-click to change profile picture"
>
  <img 
    src={profilePicture || '/Images/Manurevalogo.png'} 
    alt="Profile" 
    style={{
      width: '100%', 
      height: '100%', 
      borderRadius: '50%', 
      objectFit: 'cover'
    }}
    onError={(e) => {
      // If image fails to load, show Manureva logo
      e.target.src = '/Images/Manurevalogo.png';
    }}
  />
</div>
{/* Profile Picture Upload Modal */}
      {showProfilePictureModal && (
        <div className="consult-popup-overlay" onClick={closeProfilePictureModal}>
          <div 
            className="consult-popup-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '500px',
              width: '90%',
              borderRadius: '16px',
              padding: '0',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FaCamera style={{ color: '#667eea' }} />
                Profile Picture
              </h3>
              <button
                onClick={closeProfilePictureModal}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.2s'
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              background: 'white',
              padding: '32px 24px'
            }}>
              {/* Current Profile Picture */}
<div style={{
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '32px'
}}>
  {previewImage ? (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        overflow: 'hidden',
        border: '4px solid #667eea',
        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
      }}>
        <img 
          src={previewImage} 
          alt="Preview" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>
      <p style={{
        marginTop: '12px',
        fontSize: '14px',
        color: '#6b7280'
      }}>
        Preview
      </p>
    </div>
  ) : (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        overflow: 'hidden',
        border: '4px solid #667eea',
        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
      }}>
        <img 
          src={profilePicture || '/Images/Manurevalogo.png'} 
          alt="Current Profile" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            e.target.src = '/Images/Manurevalogo.png';
          }}
        />
      </div>
      <p style={{
        marginTop: '12px',
        fontSize: '14px',
        color: '#6b7280'
      }}>
        {profilePicture ? 'Current Picture' : 'Default Picture'}
      </p>
    </div>
  )}
</div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {previewImage ? (
                  <>
                    {/* Upload Button */}
                    <button
                      onClick={handleUploadProfilePicture}
                      disabled={isUploadingProfile}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '16px 24px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: isUploadingProfile ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        transition: 'all 0.3s',
                        opacity: isUploadingProfile ? 0.7 : 1,
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                      }}
                    >
                      {isUploadingProfile ? (
                        <>
                          <span className="spinner-border spinner-border-sm" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaUpload />
                          Save Profile Picture
                        </>
                      )}
                    </button>

                    {/* Cancel Preview */}
                    <button
                      onClick={() => setPreviewImage(null)}
                      disabled={isUploadingProfile}
                      style={{
                        background: 'white',
                        color: '#6b7280',
                        border: '2px solid #e5e7eb',
                        padding: '16px 24px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: isUploadingProfile ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s'
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {/* Upload from Device */}
                    <button
                      onClick={() => profileFileInputRef.current?.click()}
                      disabled={isUploadingProfile}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '16px 24px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: isUploadingProfile ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        transition: 'all 0.3s',
                        opacity: isUploadingProfile ? 0.7 : 1,
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                      }}
                    >
                      <FaUpload />
                      Upload from Device
                    </button>

                    {/* Use Default Logo */}
{profilePicture && (
  <button
    onClick={handleUseEmailAvatar}
    disabled={isUploadingProfile}
    style={{
      background: 'white',
      color: '#667eea',
      border: '2px solid #667eea',
      padding: '16px 24px',
      borderRadius: '12px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: isUploadingProfile ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s',
      opacity: isUploadingProfile ? 0.7 : 1
    }}
  >
    <FaUserTie />
    Use Default Logo
  </button>

                    )}
                  </>
                )}
              </div>

              {/* Hidden File Input */}
              <input
                ref={profileFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileFileChange}
                style={{ display: 'none' }}
              />

              {/* Info Text */}
              <div style={{
                marginTop: '20px',
                padding: '12px',
                background: '#f3f4f6',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#6b7280',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0 }}>
                  📸 Supported formats: JPG, PNG, GIF
                  <br />
                  Maximum file size: 5MB
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
              </div>
              <div className="consult-logout-icon" onClick={handleLogoutClick} style={{cursor: 'pointer',marginTop:'7px'}} title="Logout">
                <PowerSettingsNewIcon/>
              </div>
            </div>
          </div>

          <div className="consult-main-content">
            <div className="consult-page-header">
              <h1>My Timesheet</h1>
              <div className="consult-search-container"></div>
            </div>

            <div className="consult-center-modules">
              <div className="consult-stats-container">
                <div className="consult-stat-card">
                  <div className="consult-stat-header">
                    <span style={{color:'#6b7280'}}>This Week</span>
                    <div className="consult-stat-icon">
                      <img src="/Images/Timesheet1.png" alt="⏰" />
                    </div>
                  </div>
                  <div className="consult-stat-value">{currentWeekTotal.toFixed(1)}h</div>
                </div>
                <div className="consult-stat-card">
                  <div className="consult-stat-header">
                    <span style={{color:'#009688'}}>This Month</span>
                    <div className="consult-stat-icon">
                      <img src="Images/tabler_users.png" alt="📅" />
                    </div>
                  </div>
                  <div className="consult-stat-value">{currentMonthTotal.toFixed(1)}h</div>
                </div>
              </div>

              <div className="Consult-week-calender">
                <div className="consult-week-navigation">
                  <div className="consult-week-navigation-left">
                    <div className="consult-nav-arrow" onClick={() => navigateWeek(-1)} style={{cursor: 'pointer'}}>‹</div>
                      <div className="consult-week-info">
                        <span className="consult-week-range">
                          {formatDate(currentWeekStart)}–{formatDate(weekEnd)}, {currentWeekStart.getFullYear()}
                        </span>
                        <span className="consult-week-total" style={{color:'#6b7280'}}>
                          Week Total: {calculateWeekTotal().toFixed(1)} hours
                          {isCurrentWeekSubmitted() && (
                            <span style={{marginLeft: '10px', color: '#4CAF50', fontWeight: 'bold'}}>
                              ✓ Submitted
                            </span>
                          )}
                        </span>
                      </div>
                    <div className="consult-nav-arrow" onClick={() => navigateWeek(1)} style={{ cursor: 'pointer'}}>›</div>
                  </div>
                  
                  
                  <div className="consult-week-actions">

                      <button className="consult-copy-btn" onClick={handleCopyLastWeek}>
                        <img src="/Images/Copy.png" alt="📋" /> Copy Last Week
                      </button>
                    <button 
                      className="consult-submit-btn" 
                      onClick={handleSubmitWeek}
                      disabled={isCurrentWeekSubmitted() || !hasCurrentWeekEntries()}
                      style={{
                        opacity: (isCurrentWeekSubmitted() || !hasCurrentWeekEntries()) ? 0.5 : 1,
                        cursor: (isCurrentWeekSubmitted() || !hasCurrentWeekEntries()) ? 'not-allowed' : 'pointer',
                        backgroundColor: isCurrentWeekSubmitted() ? '#4CAF50' : '#3b82f6'
                      }}
                    >
                      <img src="Images/send.png" alt="✓" /> 
                      {isCurrentWeekSubmitted() ? '✓ Week Submitted' : 'Submit Week'}
                    </button>
                  </div>
                </div>

                <div className="consult-calendar-grid">
                  {weekDates.map((date, index) => {
                    const dateKey = getLocalDateKey(date);
                    const dayEntries = timesheetEntries[dateKey] || [];
                    const dayNames = ['Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                    const totalHours = dayEntries.reduce((sum, entry) => sum + entry.hours, 0);
                    
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isSubmitted = isCurrentWeekSubmitted();
                    
                    return (
                      <div 
                        key={index} 
                        className="consult-day-column"
                        onDoubleClick={() => handleDayColumnDoubleClick(date)}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: isToday ? '#1e3a5f' : '#ffffff',
                          opacity: isSubmitted ? 0.8 : 1
                        }}
                        title={isSubmitted ? "Week submitted - view only" : "Double-click to view saved entries for this day"}
                      >
                        <div className="consult-day-header" style={{
                          backgroundColor: isToday ? '#1e3a5f' : undefined,
                          borderRadius: isToday ? '4px' : undefined
                        }}>
                          <span className="consult-day-name" style={{color: isToday ? '#ffffffff' : undefined}}>
                            {dayNames[index]}
                          </span>
                          <span className="consult-day-number" style={{
                            color: isToday ? '#ffffffff' : undefined,
                          }}>
                            {date.getDate()}
                            {isToday && <span style={{fontSize: '10px', marginLeft: '2px'}}>TODAY</span>}
                          </span>
                        </div>
                        {dayEntries.length > 0 && (
                          <div className="consult-day-entries" style={{marginBottom: '5px', color:isToday ? '#ffffffff' : undefined}}>
                            <div style={{fontWeight: 'bold', color:isToday ? '#ffffffff' : undefined, fontSize: '12px', textAlign: 'center', marginTop: '-8px'}}>
                              Total: {totalHours.toFixed(1)}h
                            </div>
                          </div>
                        )}
                        <button 
                          className="consult-add-hours-btn" 
                          onClick={() => handleAddHoursClick(date)}
                          disabled={isSubmitted || isFutureDate(date)}
                          style={{
                            backgroundColor: isToday ? '#ffffffff' : undefined,
                            color: isToday ? '#1e3a5f' : undefined,
                            opacity: (isSubmitted || isFutureDate(date)) ? 0.5 : 1,
                            cursor: (isSubmitted || isFutureDate(date)) ? 'not-allowed' : 'pointer'
                          }}
                        > 
                          {isSubmitted ? '✓ Submitted' : '+ Add Hours'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="consult-bottom-section">
                <div className="consult-recent-activity">
                  <h3>Recent Activity</h3>
                  <div className="consult-activity-list" style={{
                    maxHeight: '250px',
                    overflowY: 'auto',
                    paddingRight: '8px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 #f7fafc'
                  }}>
                    {recentActivity.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#666', padding: '20px', fontStyle: 'italic' }}>
                        No recent activity
                      </div>
                    ) : (
                      recentActivity.map((activity, index) => {
                        const getStatusColor = (status) => {
                          switch (status) {
                            case 'approved': return 'consult-green';
                            case 'submitted': return 'consult-grey';
                            case 'draft': return 'consult-orange';
                            default: return 'consult-grey';
                          }
                        };
                        
                        const getStatusClass = (status) => {
                          switch (status) {
                            case 'approved': return 'consult-activity-status-consult-approved';
                            case 'submitted': return 'consult-activity-status-consult-submitted';
                            case 'draft': return 'consult-activity-status-consult-draft';
                            default: return 'consult-activity-status-consult-submitted';
                          }
                        };
                        
                        const formatDate = (dateStr) => {
                          const date = new Date(dateStr);
                          return date.toLocaleDateString('en-US', { 
                            month: '2-digit', 
                            day: '2-digit', 
                            year: 'numeric' 
                          });
                        };
                        
                        return (
                          <div key={activity.id || index} className="consult-activity-item">
                            <div className={`consult-activity-dot-${getStatusColor(activity.status)}`}>.</div>
                            <div className="consult-activity-details">
                              <span className="consult-activity-text">{activity.text}</span>
                              <span className="consult-activity-date">
                                {formatDate(activity.date)} • {activity.status}
                              </span>
                            </div>
                            <span className={getStatusClass(activity.status)}>{activity.status}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="consult-active-projects">
  <h3>Active Projects</h3>
  <div className="consult-project-list">
    {loadingProjects ? (
      <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
        Loading projects...
      </div>
    ) : assignedProjects.length === 0 ? (
      <div style={{ textAlign: 'center', color: '#666', padding: '20px', fontStyle: 'italic' }}>
        No projects assigned yet
      </div>
    ) : (
      assignedProjects.map((project, index) => (
        <div 
          key={project.id} 
          className="consult-project-item"
          style={{
            backgroundColor: index % 2 === 0 ? undefined : '#0062B1'
          }}
        >
          <div className="consult-project-info">
            <span className="consult-project-name">{project.name || 'N/A'}</span>
            <span className="consult-project-desc">{project.description || 'No description'}</span>
            <span className="consult-project-client">{project.client || 'N/A'}</span>
          </div>
          <div className="consult-project-hours">
            <span className="consult-hours-logged">{project.hoursLogged || '0'}h logged</span>
            <span className="consult-budget">${project.budget || '0'}</span>
          </div>
        </div>
      ))
    )}
  </div>
</div>
              </div>
            </div>
          </div>

          {/* Add Hours Popup */}
          {showPopup && (
            <div className="consult-popup-overlay" onClick={handleClosePopup}>
              <div className="consult-popup-content" onClick={(e) => e.stopPropagation()} style={{
                width: '637px',
                borderRadius: '16px',
                padding: '32px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '24px'
                }}>
                  <h2 style={{ 
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#2196F3',
                    font:'albert sans',
                  }}>Add Hours</h2>
                </div>

                <div className="consult-popup-form" style={{
                  display: 'grid',
                  gap: '24px'
                }}>
                  <div className="consult-popup-date-hours" style={{display:'flex',gap:'20px'}}>
                    <div className="consult-form-group" style={{width:'255px',height:'74px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight:'400',
                        font :'Albert Sans',
                      }}>Date <span style={{color: '#DC2626'}}>*</span></label>
                      <input
                        type="date"
                        name="date"
                        value={selectedDate ? getLocalDateKey(selectedDate) : ''}
                        readOnly
                        style={{
                          width :'255px',
                          height:'48px',
                          padding: '8px 12px',
                          border: '1px solid #ECEDF0',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight:'400',
                          font :'Albert Sans',
                          backgroundColor: '#FAFAFA',
                          color: '#555E67'
                        }}
                      />
                    </div>

                    <div className="consult-form-group" style={{width:'255px',height:'74px'}} >
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: '#555E67',
                        fontSize: '12px',
                        fontWeight:'400',
                        font :'Albert Sans',
                      }}>Hours <span style={{color: '#DC2626'}}>*</span></label>
                      <input
                        type="number"
                        name="hours"
                        min="0"
                        max="24"
                        step="0.5"
                        value={formData.hours || ''}
                        onChange={handleInputChange}
                        placeholder="Hours (0–24)"
                        style={{
                          width :'150px',
                          height:'48px',
                          padding: '8px 12px',
                          border: '1px solid #ECEDF0',
                          backgroundColor :'#FAFAFA',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight:'400',
                          font :'Albert Sans',
                          color: '#555E67',
                        }}
                      />
                    </div>
                  </div>

                  <div className="consult-form-group">
  <label style={{
    display: 'block',
    marginBottom: '8px',
    color: '#555E67',
    fontSize: '12px',
    fontWeight:'400',
    font :'Albert Sans',
  }}>Project Title <span style={{color: '#DC2626'}}>*</span></label>
  <select
    name="projectTitle"
    value={formData.projectTitle}
    onChange={handleInputChange}
    disabled={loadingProjects}
    style={{
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #ECEDF0',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight:'400',
      font :'Albert Sans',
      backgroundColor:'#FAFAFA',
      color: '#555E67',
      cursor: loadingProjects ? 'wait' : 'pointer'
    }}
  >
    <option value="">
      {loadingProjects ? 'Loading projects...' : 'Select project'}
    </option>
    {assignedProjects.length > 0 ? (
      assignedProjects.map((project) => (
        <option key={project.id} value={project.name}>
          {project.name} - {project.client}
        </option>
      ))
    ) : (
      !loadingProjects && <option disabled>No projects assigned</option>
    )}
  </select>
  {assignedProjects.length === 0 && !loadingProjects && (
    <small style={{ 
      color: '#999', 
      fontSize: '11px', 
      marginTop: '4px', 
      display: 'block' 
    }}>
      No projects assigned to you yet. Contact your HR.
    </small>
  )}
</div>

                  <div className="consult-form-group" style={{width:'597px',height:"136px",gap:'16px'}}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      color: '#555E67',
                      fontSize: '14px',
                      fontWeight:'400',
                      font:'albert sans',
                    }}>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="please provide a detailed reason for Working hours "
                      style={{
                        width:'597px',
                        height:'110px',
                        padding: '8px 12px',
                        border: '1px solid #ECEDF0',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight:'400',
                        font :'Albert Sans',
                        resize: 'vertical',
                        backgroundColor:'#FAFAFA',
                        color: '#555E67',
                      }}
                    />
                  </div>

                  <button 
                    className="consult-btn-Apply" 
                    onClick={handleSave}
                    style={{
                      width:'123px',
                      height:'35px',
                      padding: '8px 16px',
                      borderRadius: '28px',
                      background: '#23b84eff',
                      color: 'white',
                      fontSize: '12px',
                      font: 'Albert Sans',
                      fontWeight: '600',
                      marginLeft:'12cm',
                      border: 'none',
                      cursor: 'pointer',
                      opacity: !selectedDate || !formData.hours || !formData.projectTitle ? 0.5 : 1
                    }}
                    disabled={!selectedDate || !formData.hours || !formData.projectTitle}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Day View Popup */}
          {showDayView && selectedDate && (
            <div className="consult-popup-overlay" onClick={handleCloseDayView}>
              <div className="consult-popup-content" onClick={(e) => e.stopPropagation()}>
                <h2>Timesheet Entries - {selectedDate.toLocaleDateString()}</h2>
                
                {(() => {
                  const dateKey = getLocalDateKey(selectedDate);
                  const dayEntries = timesheetEntries[dateKey] || [];
                  const totalHours = dayEntries.reduce((sum, entry) => sum + entry.hours, 0);
                  const isSubmitted = isCurrentWeekSubmitted();
                  
                  return (
                    <div>
                      <div style={{
                        background: '#e8f5e8',
                        color: '#2e7d32',
                        padding: '10px',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}>
                        Total Hours: {totalHours.toFixed(1)}h
                        {isSubmitted && (
                          <span style={{marginLeft: '10px', color: '#4CAF50'}}>
                            ✓ Week Submitted
                          </span>
                        )}
                      </div>
                      
                      {dayEntries.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          color: '#666',
                          padding: '20px',
                          fontStyle: 'italic'
                        }}>
                          No timesheet entries found for this date.
                        </div>
                      ) : (
                        <div className="consult-day-entries-list">
                          {dayEntries.map((entry, index) => (
                            <div key={entry.id} style={{
                              background: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '8px',
                              padding: '15px',
                              marginBottom: '10px',
                              position: 'relative'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '10px'
                              }}>
                                <div>
                                  <h4 style={{ margin: '0 0 5px 0', color: '#2196F3' }}>
                                    {entry.projectTitle}
                                  </h4>
                                  <div style={{ color: '#666', fontSize: '14px' }}>
                                    <strong>Time:</strong> {entry.fromTime} - {entry.toTime} 
                                    <span style={{ color: '#2196F3', fontWeight: 'bold', marginLeft: '10px' }}>
                                      ({entry.hours.toFixed(1)}h)
                                    </span>
                                  </div>
                                  {entry.description && (
                                    <div style={{ 
                                      color: '#555', 
                                      fontSize: '14px', 
                                      marginTop: '8px',
                                      fontStyle: 'italic'
                                    }}>
                                      <strong>Description:</strong> {entry.description}
                                    </div>
                                  )}
                                  <div style={{
                                    marginTop: '8px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    color: entry.status === 'approved' ? '#4caf50' : 
                                           entry.status === 'submitted' ? '#ff9800' : '#757575'
                                  }}>
                                    Status: {entry.status.toUpperCase()}
                                  </div>
                                </div>
                                {!isSubmitted && entry.status === 'draft' && (
                                  <div style={{ display: 'flex', gap: '5px' }}>
                                    <button
                                      onClick={() => handleEditEntry(entry, selectedDate)}
                                      style={{
                                        background: '#2196F3',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '30px',
                                        height: '30px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      title="Edit this entry"
                                    >
                                      ✎
                                    </button>
                                    <button
                                      onClick={() => handleDeleteEntry(dateKey, entry.id)}
                                      style={{
                                        background: '#ff4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '30px',
                                        height: '30px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      title="Delete this entry"
                                    >
                                      ×
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '12px',
                                color: '#888'
                              }}>
                                <span>Entry #{index + 1}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="consult-popup-buttons" style={{ marginTop: '20px' }}>
                        {!isSubmitted && !isFutureDate(selectedDate) && (
                          <button 
                            className="consult-btn-save" 
                            onClick={() => {
                              handleCloseDayView();
                              handleAddHoursClick(selectedDate);
                            }}
                            style={{
                              padding: '10px 20px',
                              background: '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '10px'
                            }}
                          >
                            + Add More Hours
                          </button>
                        )}
                        <button 
                          className="consult-btn-cancel" 
                          onClick={handleCloseDayView}
                          style={{
                            padding: '10px 20px',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Edit Entry Popup */}
          {showEditPopup && editingEntry && selectedDate && (
            <div className="consult-popup-overlay" onClick={handleCloseEditPopup}>
              <div className="consult-popup-content" onClick={(e) => e.stopPropagation()} style={{
                width: '550px',
                borderRadius: '16px',
                padding: '32px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '24px'
                }}>
                  <h2 style={{ 
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#1F2937'
                  }}>Edit Timesheet Entry</h2>
                </div>

                <div className="consult-popup-form" style={{
                  display: 'grid',
                  gap: '24px'
                }}>
                  <div className="consult-form-date-hours" style={{display:'flex'}} >
                    <div className="consult-form-group">
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: '#374151',
                        fontSize: '14px'
                      }}>Date <span style={{color: '#DC2626'}}>*</span></label>
                      <input
                        type="date"
                        name="date"
                        value={selectedDate ? getLocalDateKey(selectedDate) : ''}
                        readOnly
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '14px',
                          backgroundColor: '#F9FAFB'
                        }}
                      />
                    </div>

                    <div className="consult-form-group" style={{marginLeft: '20px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: '#374151',
                        fontSize: '12px'
                      }}>Hours <span style={{color: '#DC2626'}}>*</span></label>
                      <input
                        type="number"
                        name="hours"
                        min="0"
                        max="24"
                        step="0.5"
                        value={formData.hours || ''}
                        onChange={handleInputChange}
                        placeholder="Hours (0–24)"
                        style={{
                          width: '150px',
                          padding: '8px 10px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                    </div>
                  </div>

                  <div className="consult-form-group">
  <label style={{
    display: 'block',
    marginBottom: '8px',
    color: '#374151',
    fontSize: '14px'
  }}>Project Title <span style={{color: '#DC2626'}}>*</span></label>
  <select
    name="projectTitle"
    value={formData.projectTitle}
    onChange={handleInputChange}
    disabled={loadingProjects}
    style={{
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      fontSize: '14px',
      cursor: loadingProjects ? 'wait' : 'pointer'
    }}
  >
    <option value="">
      {loadingProjects ? 'Loading projects...' : 'Select project'}
    </option>
    {assignedProjects.length > 0 ? (
      assignedProjects.map((project) => (
        <option key={project.id} value={project.name}>
          {project.name} - {project.client}
        </option>
      ))
    ) : (
      !loadingProjects && <option disabled>No projects assigned</option>
    )}
  </select>
</div>

                  <div className="consult-form-group">
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      color: '#374151',
                      fontSize: '14px'
                    }}>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="What did you work on?"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  <div className="consult-popup-buttons" style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '16px'
                  }}>
                    <button 
                      className="consult-btn-cancel" 
                      onClick={handleCloseEditPopup}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        background: 'white',
                        color: '#374151',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="consult-btn-save" 
                      onClick={handleUpdateEntry}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        background: '#2196F3',
                        color: 'white',
                        fontSize: '14px',
                        cursor: 'pointer',
                        opacity: !selectedDate || !formData.hours || !formData.projectTitle ? 0.5 : 1
                      }}
                      disabled={!selectedDate || !formData.hours || !formData.projectTitle}
                    >
                      Update Entry
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TimesheetDashboard;